import type {
  PlatformCatalogProduct,
  PlatformCatalogResource,
  PlatformEntitledDataset,
  PlatformProductMetadata,
} from "@/types/platform.types";

export const getCatalogResourceCount = (product: PlatformCatalogProduct) =>
  product.resourceCount ?? product.resources?.length ?? 0;

export const isMultiResourceCatalogProduct = (product: PlatformCatalogProduct) => getCatalogResourceCount(product) > 1;

export type PlatformProviderLogo =
  | string
  | {
      url: string;
      darkUrl: string;
      altText?: string;
    }
  | null;

const getMetadataValue = (metadata: PlatformProductMetadata | null | undefined, key: string) => {
  const value = metadata ? (metadata as Record<string, unknown>)[key] : null;
  return typeof value === "string" ? value : null;
};

export const getProductCategory = (metadata: PlatformProductMetadata | null | undefined) =>
  getMetadataValue(metadata, "category") ?? "Platform product";

const resolveDocsProviderPiece = (providerName: string) => {
  const lower = providerName.toLowerCase();
  return lower === "core" || lower === "premium" ? lower : "datasets";
};

/** Build `/docs/{provider}/{datasetId}` from resource metadata (matches server `getDatasetDocsUrl`). */
export const buildDatasetDocsUrlFromMetadata = (
  metadata: PlatformProductMetadata | null | undefined,
  datasetIdOverride?: string | null
): string | null => {
  const datasetId =
    (typeof datasetIdOverride === "string" && datasetIdOverride.trim()) || getMetadataValue(metadata, "datasetId");
  if (!datasetId?.trim()) return null;
  const providerPiece = resolveDocsProviderPiece(getProviderName(metadata));
  return `/docs/${providerPiece}/${datasetId.trim()}`;
};

const docsUrlReferencesDatasetId = (docsUrl: string, datasetId: string) => {
  const id = datasetId.trim().toLowerCase();
  return docsUrl.toLowerCase().includes(`/${id}`);
};

export const getDocsUrl = (
  metadata: PlatformProductMetadata | null | undefined,
  options?: { datasetId?: string | null }
) => {
  const stored = getMetadataValue(metadata, "docsUrl");
  const derived = buildDatasetDocsUrlFromMetadata(metadata, options?.datasetId);

  if (stored && /^https?:\/\//i.test(stored)) {
    return stored;
  }

  const datasetId =
    (typeof options?.datasetId === "string" && options.datasetId.trim()) || getMetadataValue(metadata, "datasetId");

  if (derived) {
    if (!stored) return derived;
    if (datasetId && !docsUrlReferencesDatasetId(stored, datasetId)) {
      return derived;
    }
  }

  return stored ?? derived;
};

export const getDatasetTypeLabel = (metadata: PlatformProductMetadata | null | undefined) => {
  const datasetProperties = metadata?.datasetProperties;
  const datasetType =
    datasetProperties && typeof datasetProperties.datasetType === "string"
      ? datasetProperties.datasetType
      : getMetadataValue(metadata, "datasetType");
  return datasetType ?? "Dataset";
};

export const getProviderName = (metadata: PlatformProductMetadata | null | undefined) => {
  const name =
    getMetadataValue(metadata?.providerMetadata, "name") ??
    getMetadataValue(metadata, "providerName") ??
    getMetadataValue(metadata, "provider");
  const trimmed = name?.trim();
  return trimmed || "viaNexus";
};

const isPlatformProviderLogoObject = (
  value: unknown
): value is Exclude<PlatformProviderLogo, string | null> => {
  if (typeof value !== "object" || value == null) return false;
  const logo = value as Record<string, unknown>;
  if (typeof logo.url !== "string" || typeof logo.darkUrl !== "string") return false;
  return logo.altText == null || typeof logo.altText === "string";
};

export const getProviderLogo = (metadata: PlatformProductMetadata | null | undefined): PlatformProviderLogo => {
  const providerLogo = metadata?.providerMetadata?.logo;
  if (typeof providerLogo === "string") return providerLogo;
  if (isPlatformProviderLogoObject(providerLogo)) return providerLogo;
  return (
    getMetadataValue(metadata, "providerLogoUrl") ??
    getMetadataValue(metadata, "logoUrl") ??
    getMetadataValue(metadata, "imageUrl")
  );
};

const formatEntitlementValue = (value: number | string, unit: string | null | undefined) =>
  unit?.trim() ? `${value} ${unit}` : String(value);

export interface TierOrDealEntitlement {
  id?: number | string;
  entitlementKey?: string | null;
  entitlementName?: string;
  key?: string;
  name?: string;
  numericValue?: number | null;
  stringValue?: string | null;
  jsonValue?: unknown | null;
  booleanValue?: boolean | null;
  displayValue?: string | null;
  unit?: string | null;
}

export const tierEntitlementHasValue = (entitlement: TierOrDealEntitlement) => {
  if (entitlement.numericValue != null) return Boolean(entitlement.numericValue);
  if (entitlement.stringValue != null) return Boolean(entitlement.stringValue.trim());
  if (entitlement.jsonValue != null) return true;
  if (entitlement.booleanValue != null) return entitlement.booleanValue;
  return Boolean(entitlement.displayValue?.trim());
};

export const getTierEntitlementDisplayValue = (entitlement: TierOrDealEntitlement) => {
  if (entitlement.displayValue?.trim()) return entitlement.displayValue;
  if (entitlement.numericValue != null) return formatEntitlementValue(entitlement.numericValue, entitlement.unit);
  if (entitlement.stringValue?.trim()) return formatEntitlementValue(entitlement.stringValue, entitlement.unit);
  return null;
};

interface ProductWithDiscount {
  discount?: {
    percent?: number | null;
  } | null;
}

export const getMonthlyPrice = (tier: { pricing: { amount: string | number } | null }) => Number(tier.pricing?.amount ?? 0);

export const getDiscountedPrice = (product: ProductWithDiscount, amount: number) => {
  if (!product.discount?.percent) return null;
  return amount * (1 - product.discount.percent / 100);
};

/** Checkout/cart price — discounted when the product has a use-case discount. */
export const getEffectiveMonthlyPrice = (product: ProductWithDiscount, amount: number) =>
  getDiscountedPrice(product, amount) ?? amount;

/** Cart line total — always discounts from tier list price when tier pricing is available. */
export const getPlatformProductCartLinePrice = (item: {
  monthlyPrice: number;
  tierId: number;
  product: ProductWithDiscount & {
    tiers: Array<{ id: number; pricing: { amount: string | number } | null }>;
  };
}) => {
  const tier = item.product.tiers.find(t => t.id === item.tierId);
  const listAmount = tier?.pricing ? getMonthlyPrice(tier) : item.monthlyPrice;
  return getEffectiveMonthlyPrice(item.product, listAmount);
};

/** Edge catalog card: tier row matching the selected membership slug, if any. */
export const getEdgeTierForMembershipSlug = (
  product: {
    tiers: Array<{
      id?: number;
      slug: string;
      displayName?: string | null;
      name?: string;
      pricing: { amount: string | number } | null;
      entitlements?: TierOrDealEntitlement[];
    }>;
  },
  tierSlug: string | null | undefined
) => {
  if (!tierSlug) return null;
  return product.tiers.find(t => t.slug === tierSlug) ?? null;
};

export const isEdgePriceContactUs = (amount: number) => !Number.isFinite(amount) || amount <= 0;

/** Tiered product that gates edge dataset pricing (membership flag or parent of edge products). */
export const isMembershipGatingProduct = (
  product: { id: number; type: string; isMembershipProduct?: boolean },
  allProducts: Array<{ type: string; parentProductId?: number | null }>
) => Boolean(product.isMembershipProduct || allProducts.some(p => p.type === "edge" && p.parentProductId === product.id));

export interface MembershipContext {
  productId: number;
  tierId: number;
  tierSlug: string;
}

/** Active membership subscription tier slug, when the org is subscribed to a gating product. */
export const resolveMembershipContextFromSubscriptions = (
  subscriptions: Array<{
    productId: number;
    tierId: number;
    status: string;
    endsAt?: string | null;
    product?: { isMembershipProduct?: boolean; type?: string } | null;
    tier?: { slug?: string } | null;
  }>,
  catalogProducts: Array<{ id: number; type: string; parentProductId?: number | null; isMembershipProduct?: boolean }>
): MembershipContext | null => {
  const active = subscriptions.filter(sub => sub.status === "active" && !sub.endsAt);
  const edgeParentIds = new Set(
    catalogProducts.filter(p => p.type === "edge" && p.parentProductId != null).map(p => p.parentProductId!)
  );

  for (const sub of active) {
    const catalogProduct = catalogProducts.find(p => p.id === sub.productId);
    const isGating =
      sub.product?.isMembershipProduct ||
      catalogProduct?.isMembershipProduct ||
      edgeParentIds.has(sub.productId);
    if (!isGating) continue;

    const tierSlug = sub.tier?.slug;
    if (!tierSlug) continue;

    return { productId: sub.productId, tierId: sub.tierId, tierSlug };
  }

  return null;
};

/** Edge tier list price for a membership slug (before use-case discount). */
export const getEdgeTierListPrice = (
  edgeProduct: ProductWithDiscount & {
    tiers: Array<{ slug: string; pricing: { amount: string | number } | null }>;
  },
  membershipTierSlug: string | null | undefined
) => {
  const tier = getEdgeTierForMembershipSlug(edgeProduct, membershipTierSlug);
  if (!tier?.pricing) return null;
  const amount = getMonthlyPrice(tier);
  if (isEdgePriceContactUs(amount)) return null;
  return amount;
};

/** Effective edge tier price for a membership slug (after use-case discount). */
export const getEdgeTierEffectivePrice = (
  edgeProduct: ProductWithDiscount & {
    tiers: Array<{ slug: string; pricing: { amount: string | number } | null }>;
  },
  membershipTierSlug: string | null | undefined
) => {
  const amount = getEdgeTierListPrice(edgeProduct, membershipTierSlug);
  if (amount == null) return null;
  return getEffectiveMonthlyPrice(edgeProduct, amount);
};

export interface PendingUpgradeEdgeLine {
  productId: number;
  name: string;
  /** Monthly price on the target membership tier, or null when the dataset is not offered there. */
  newMonthlyPrice: number | null;
}

/**
 * Edge datasets the org is subscribed to that get repriced when its membership tier
 * changes. Used by the checkout order summary and the projected-monthly calculation so
 * a membership upgrade shows (and totals) every edge line it cascades to, not just the
 * membership row. A null `newMonthlyPrice` means the dataset isn't available on the new tier.
 */
export const getPendingUpgradeEdgeLines = (
  catalog: PlatformCatalogProduct[],
  subscriptions: Array<{ productId: number; status: string }>,
  pendingUpgrade: { productId: number; newTierId: number } | null | undefined
): PendingUpgradeEdgeLine[] => {
  if (!pendingUpgrade) return [];
  const membership = catalog.find(product => product.id === pendingUpgrade.productId);
  const newTierSlug = membership?.tiers.find(tier => tier.id === pendingUpgrade.newTierId)?.slug ?? null;
  const productsById = new Map(catalog.map(product => [product.id, product]));
  const seen = new Set<number>();
  const lines: PendingUpgradeEdgeLine[] = [];
  for (const subscription of subscriptions) {
    if (subscription.status !== "active") continue;
    const product = productsById.get(subscription.productId);
    if (!product || product.type !== "edge" || product.parentProductId !== pendingUpgrade.productId) continue;
    if (seen.has(product.id)) continue;
    seen.add(product.id);
    lines.push({
      productId: product.id,
      name: product.name,
      newMonthlyPrice: getEdgeTierEffectivePrice(product, newTierSlug),
    });
  }
  return lines;
};

const catalogResourceMatchesSearch = (resource: PlatformCatalogResource, q: string) => {
  const name = resource.name.toLowerCase();
  const desc = (resource.description ?? "").toLowerCase();
  const category = getProductCategory(resource.metadata).toLowerCase();
  const typeLabel = getDatasetTypeLabel(resource.metadata).toLowerCase();
  const provider = getProviderName(resource.metadata).toLowerCase();
  return (
    name.includes(q) ||
    desc.includes(q) ||
    category.includes(q) ||
    typeLabel.includes(q) ||
    provider.includes(q)
  );
};

/** Unique category labels from a product's resources, or product metadata when there are no resources. */
export const getCatalogProductCategoryLabels = (product: PlatformCatalogProduct): string[] => {
  const resources = product.resources ?? [];
  if (resources.length > 0) {
    return [...new Set(resources.map(resource => getProductCategory(resource.metadata)))];
  }
  return [getProductCategory(product.metadata)];
};

/** Sorted unique category labels across all catalog products (includes every bundled dataset category). */
export const collectCatalogCategoryLabels = (products: PlatformCatalogProduct[]) => {
  const labels = new Set<string>();
  for (const product of products) {
    for (const label of getCatalogProductCategoryLabels(product)) {
      labels.add(label);
    }
  }
  return Array.from(labels).sort((a, b) => a.localeCompare(b));
};

export interface CatalogCategoryOption {
  label: string;
  count: number;
  /** Provider logo for the option, when the dimension is "provider" and a logo is available. */
  logo?: PlatformProviderLogo;
}

/** Empty selection means "no category filter" (all match). Otherwise OR semantics across the selected set. */
const matchesCategorySelection = (categoryName: string, selectedCategories: string[]) =>
  selectedCategories.length === 0 || selectedCategories.includes(categoryName);

export const catalogProductMatchesCategory = (product: PlatformCatalogProduct, selectedCategories: string[]) => {
  if (selectedCategories.length === 0) return true;
  return getCatalogProductCategoryLabels(product).some(label => selectedCategories.includes(label));
};

/** Unique provider names from a product's resources, or product metadata when there are no resources. */
export const getCatalogProductProviderLabels = (product: PlatformCatalogProduct): string[] => {
  const resources = product.resources ?? [];
  if (resources.length > 0) {
    return [...new Set(resources.map(resource => getProviderName(resource.metadata)))];
  }
  return [getProviderName(product.metadata)];
};

/** Empty selection means "no provider filter" (all match). Otherwise OR semantics across the selected set. */
const matchesProviderSelection = (providerName: string, selectedProviders: string[]) =>
  selectedProviders.length === 0 || selectedProviders.includes(providerName);

export const catalogProductMatchesProvider = (product: PlatformCatalogProduct, selectedProviders: string[]) => {
  if (selectedProviders.length === 0) return true;
  return getCatalogProductProviderLabels(product).some(label => selectedProviders.includes(label));
};

export const catalogProductMatchesSearch = (product: PlatformCatalogProduct, searchQuery: string) => {
  const q = searchQuery.trim().toLowerCase();
  if (!q) return true;

  const name = product.name.toLowerCase();
  const desc = (product.description ?? "").toLowerCase();
  const slug = product.slug.toLowerCase();
  if (name.includes(q) || desc.includes(q) || slug.includes(q)) return true;

  const resources = product.resources ?? [];
  if (resources.length > 0) {
    return resources.some(resource => catalogResourceMatchesSearch(resource, q));
  }

  const category = getProductCategory(product.metadata).toLowerCase();
  const datasetLabel = getDatasetTypeLabel(product.metadata).toLowerCase();
  const provider = getProviderName(product.metadata).toLowerCase();
  return category.includes(q) || datasetLabel.includes(q) || provider.includes(q);
};

const catalogResourceMatchesFilters = (
  resource: PlatformCatalogResource,
  searchQuery: string,
  selectedCategories: string[],
  selectedProviders: string[]
) => {
  if (!matchesCategorySelection(getProductCategory(resource.metadata), selectedCategories)) {
    return false;
  }
  if (!matchesProviderSelection(getProviderName(resource.metadata), selectedProviders)) {
    return false;
  }
  const q = searchQuery.trim().toLowerCase();
  if (q && !catalogResourceMatchesSearch(resource, q)) {
    return false;
  }
  return true;
};

const catalogProductMetadataMatchesFilters = (
  product: PlatformCatalogProduct,
  searchQuery: string,
  selectedCategories: string[],
  selectedProviders: string[]
) => {
  if (!matchesCategorySelection(getProductCategory(product.metadata), selectedCategories)) {
    return false;
  }
  if (!matchesProviderSelection(getProviderName(product.metadata), selectedProviders)) {
    return false;
  }
  return catalogProductMatchesSearch(product, searchQuery);
};

export interface CatalogFilterCountResult {
  options: CatalogCategoryOption[];
  total: number;
}

/** Faceted dataset counts for one filter dimension (category or provider). */
const collectCatalogFilterDimensionCounts = (
  products: PlatformCatalogProduct[],
  searchQuery: string,
  selectedCategories: string[],
  selectedProviders: string[],
  getLabel: (metadata: PlatformProductMetadata | null | undefined) => string,
  excludeDimension: "category" | "provider",
  getLogo?: (metadata: PlatformProductMetadata | null | undefined) => PlatformProviderLogo
): CatalogFilterCountResult => {
  const category = excludeDimension === "category" ? [] : selectedCategories;
  const provider = excludeDimension === "provider" ? [] : selectedProviders;
  const counts = new Map<string, number>();
  const logos = new Map<string, PlatformProviderLogo>();
  let total = 0;

  const record = (metadata: PlatformProductMetadata | null | undefined) => {
    total++;
    const label = getLabel(metadata);
    counts.set(label, (counts.get(label) ?? 0) + 1);
    if (getLogo && !logos.get(label)) {
      const logo = getLogo(metadata);
      if (logo) logos.set(label, logo);
    }
  };

  for (const product of products) {
    const resources = product.resources ?? [];
    if (resources.length > 0) {
      for (const resource of resources) {
        if (!catalogResourceMatchesFilters(resource, searchQuery, category, provider)) continue;
        record(resource.metadata);
      }
    } else if (catalogProductMetadataMatchesFilters(product, searchQuery, category, provider)) {
      record(product.metadata);
    }
  }

  return {
    total,
    options: Array.from(counts.entries())
      .sort(([a], [b]) => a.localeCompare(b))
      .map(([label, count]) => ({ label, count, logo: logos.get(label) })),
  };
};

/** Category labels with dataset counts, respecting search and the active provider filter. */
export const collectCatalogCategoryOptions = (
  products: PlatformCatalogProduct[],
  searchQuery = "",
  selectedProviders: string[] = []
): CatalogCategoryOption[] =>
  getCatalogCategoryFilterCounts(products, searchQuery, selectedProviders).options;

export const getCatalogCategoryFilterCounts = (
  products: PlatformCatalogProduct[],
  searchQuery = "",
  selectedProviders: string[] = []
): CatalogFilterCountResult =>
  collectCatalogFilterDimensionCounts(
    products,
    searchQuery,
    [],
    selectedProviders,
    getProductCategory,
    "category"
  );

/** Provider names with dataset counts, respecting search and the active category filter. */
export const collectCatalogProviderOptions = (
  products: PlatformCatalogProduct[],
  searchQuery = "",
  selectedCategories: string[] = []
): CatalogCategoryOption[] =>
  getCatalogProviderFilterCounts(products, searchQuery, selectedCategories).options;

export const getCatalogProviderFilterCounts = (
  products: PlatformCatalogProduct[],
  searchQuery = "",
  selectedCategories: string[] = []
): CatalogFilterCountResult =>
  collectCatalogFilterDimensionCounts(
    products,
    searchQuery,
    selectedCategories,
    [],
    getProviderName,
    "provider",
    getProviderLogo
  );

/** Total datasets matching all active catalog filters. */
export const getCatalogDatasetCount = (
  products: PlatformCatalogProduct[],
  searchQuery = "",
  selectedCategories: string[] = [],
  selectedProviders: string[] = []
) => {
  let total = 0;
  for (const product of products) {
    const resources = product.resources ?? [];
    if (resources.length > 0) {
      for (const resource of resources) {
        if (catalogResourceMatchesFilters(resource, searchQuery, selectedCategories, selectedProviders)) {
          total++;
        }
      }
    } else if (catalogProductMetadataMatchesFilters(product, searchQuery, selectedCategories, selectedProviders)) {
      total++;
    }
  }
  return total;
};

/** Resources visible inside a bundle after category/provider/search filters (all resources when no filters active). */
export const filterCatalogProductResources = (
  product: PlatformCatalogProduct,
  searchQuery: string,
  selectedCategories: string[] = [],
  selectedProviders: string[] = []
): PlatformCatalogResource[] => {
  const resources = product.resources ?? [];
  if (resources.length === 0) return resources;

  const q = searchQuery.trim().toLowerCase();
  const hasSearch = q.length > 0;
  const hasCategory = selectedCategories.length > 0;
  const hasProvider = selectedProviders.length > 0;

  if (!hasSearch && !hasCategory && !hasProvider) return resources;

  return resources.filter(resource =>
    catalogResourceMatchesFilters(resource, searchQuery, selectedCategories, selectedProviders)
  );
};

export const catalogProductMatchesFilters = (
  product: PlatformCatalogProduct,
  searchQuery: string,
  selectedCategories: string[] = [],
  selectedProviders: string[] = []
) => {
  const resources = product.resources ?? [];
  if (resources.length > 0) {
    if (
      resources.some(resource =>
        catalogResourceMatchesFilters(resource, searchQuery, selectedCategories, selectedProviders)
      )
    ) {
      return true;
    }

    const q = searchQuery.trim().toLowerCase();
    if (!q) return false;

    const nameMatch =
      product.name.toLowerCase().includes(q) ||
      (product.description ?? "").toLowerCase().includes(q) ||
      product.slug.toLowerCase().includes(q);
    return (
      nameMatch &&
      catalogProductMatchesCategory(product, selectedCategories) &&
      catalogProductMatchesProvider(product, selectedProviders)
    );
  }

  return catalogProductMetadataMatchesFilters(product, searchQuery, selectedCategories, selectedProviders);
};

const entitledDatasetMatchesSearch = (row: PlatformEntitledDataset, q: string) => {
  const { dataset, grant } = row;
  const name = dataset.name.toLowerCase();
  const desc = (dataset.description ?? "").toLowerCase();
  const productName = grant.productName.toLowerCase();
  const category = getProductCategory(dataset.metadata).toLowerCase();
  const typeLabel = getDatasetTypeLabel(dataset.metadata).toLowerCase();
  const provider = getProviderName(dataset.metadata).toLowerCase();
  return (
    name.includes(q) ||
    desc.includes(q) ||
    productName.includes(q) ||
    category.includes(q) ||
    typeLabel.includes(q) ||
    provider.includes(q)
  );
};

/** Faceted entitled-dataset counts for category or provider filter rows. */
const collectEntitledDatasetFilterDimensionCounts = (
  rows: PlatformEntitledDataset[],
  searchQuery: string,
  selectedCategories: string[],
  selectedProviders: string[],
  getLabel: (metadata: PlatformProductMetadata | null | undefined) => string,
  excludeDimension: "category" | "provider",
  getLogo?: (metadata: PlatformProductMetadata | null | undefined) => PlatformProviderLogo
): CatalogFilterCountResult => {
  const category = excludeDimension === "category" ? [] : selectedCategories;
  const provider = excludeDimension === "provider" ? [] : selectedProviders;
  const counts = new Map<string, number>();
  const logos = new Map<string, PlatformProviderLogo>();
  let total = 0;

  for (const row of rows) {
    if (!entitledDatasetMatchesFilters(row, searchQuery, category, provider)) continue;
    total++;
    const label = getLabel(row.dataset.metadata);
    counts.set(label, (counts.get(label) ?? 0) + 1);
    if (getLogo && !logos.get(label)) {
      const logo = getLogo(row.dataset.metadata);
      if (logo) logos.set(label, logo);
    }
  }

  return {
    total,
    options: Array.from(counts.entries())
      .sort(([a], [b]) => a.localeCompare(b))
      .map(([label, count]) => ({ label, count, logo: logos.get(label) })),
  };
};

export const entitledDatasetMatchesFilters = (
  row: PlatformEntitledDataset,
  searchQuery: string,
  selectedCategories: string[],
  selectedProviders: string[] = []
) => {
  const metadata = row.dataset.metadata;
  if (!matchesCategorySelection(getProductCategory(metadata), selectedCategories)) {
    return false;
  }
  if (!matchesProviderSelection(getProviderName(metadata), selectedProviders)) {
    return false;
  }
  const q = searchQuery.trim().toLowerCase();
  if (q && !entitledDatasetMatchesSearch(row, q)) {
    return false;
  }
  return true;
};

export const getEntitledDatasetCategoryFilterCounts = (
  rows: PlatformEntitledDataset[],
  searchQuery = "",
  selectedProviders: string[] = []
): CatalogFilterCountResult =>
  collectEntitledDatasetFilterDimensionCounts(
    rows,
    searchQuery,
    [],
    selectedProviders,
    getProductCategory,
    "category"
  );

export const getEntitledDatasetProviderFilterCounts = (
  rows: PlatformEntitledDataset[],
  searchQuery = "",
  selectedCategories: string[] = []
): CatalogFilterCountResult =>
  collectEntitledDatasetFilterDimensionCounts(
    rows,
    searchQuery,
    selectedCategories,
    [],
    getProviderName,
    "provider",
    getProviderLogo
  );

export const collectEntitledDatasetProviderOptions = (
  rows: PlatformEntitledDataset[],
  searchQuery = "",
  selectedCategories: string[] = []
): CatalogCategoryOption[] =>
  getEntitledDatasetProviderFilterCounts(rows, searchQuery, selectedCategories).options;
